import React, { useEffect, useState } from 'react';
import clientService from '../../../services/clientService';
import { Filter, Calendar, CheckCircle, AlertTriangle } from 'lucide-react';

const PaymentsPending = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [onlyOverdue, setOnlyOverdue] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const params = {
        status: onlyOverdue ? 'overdue' : 'pending',
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        per_page: 500
      };
      const res = await clientService.getPayments(params);
      setRows(res.data || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  const markPaid = async (id) => {
    if (!window.confirm('Mark this payment as PAID?')) return;
    try {
      await clientService.updatePayment(id, { status: 'paid' });
      setRows(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      alert('Failed to update payment');
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-primary mb-2">Pending Payments</h1>
        <p className="text-gray-600">Review pending and overdue payments, and mark as paid</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="text-sm text-gray-600">From</label>
            <input type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} className="w-full px-3 py-2 border-2 rounded-lg"/>
          </div>
          <div>
            <label className="text-sm text-gray-600">To</label>
            <input type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} className="w-full px-3 py-2 border-2 rounded-lg"/>
          </div>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={onlyOverdue} onChange={(e)=>setOnlyOverdue(e.target.checked)} />
              Show only overdue
            </label>
          </div>
          <div className="flex items-end">
            <button onClick={load} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center gap-2"><Filter className="w-4 h-4"/> Apply</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-primary to-primary-700">
          <h2 className="text-xl font-bold text-white">{onlyOverdue ? 'Overdue' : 'Pending'} Payments ({rows.length})</h2>
        </div>
        {loading ? (
          <div className="p-10 text-center">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Invoice</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Payment Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.length === 0 ? (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No records</td></tr>
                ) : (
                  rows.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-semibold flex items-center gap-2">
                        {r.status === 'overdue' && <AlertTriangle className="w-4 h-4 text-red-600"/>}
                        {r.invoice_number}
                      </td>
                      <td className="px-6 py-4">{r.client?.company_name || '—'}</td>
                      <td className="px-6 py-4">฿{Number(r.amount).toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm flex items-center gap-2"><Calendar className="w-4 h-4"/>{r.payment_date}</td>
                      <td className="px-6 py-4 text-sm">{r.due_date || '—'}</td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={()=>markPaid(r.id)} className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded inline-flex items-center gap-1">
                          <CheckCircle className="w-4 h-4"/> Mark Paid
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsPending;
